import { DiscordMark } from "@/components/ui/discord-mark";
import type { ComparisonTool, PricingTier } from "@/components/ui/pricing-section";

function SilverMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="none" aria-hidden="true">
      <rect x="2.5" y="14.5" width="5" height="6.5" rx="1" fill="currentColor" />
      <rect x="9.5" y="10.5" width="5" height="10.5" rx="1" fill="currentColor" fillOpacity="0.17" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1" />
      <rect x="16.5" y="7" width="5" height="14" rx="1" fill="currentColor" fillOpacity="0.17" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1" />
    </svg>
  );
}

function GoldMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="none" aria-hidden="true">
      <rect x="2.5" y="14.5" width="5" height="6.5" rx="1" fill="currentColor" />
      <rect x="9.5" y="10.5" width="5" height="10.5" rx="1" fill="currentColor" />
      <rect x="16.5" y="7" width="5" height="14" rx="1" fill="currentColor" fillOpacity="0.17" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1" />
    </svg>
  );
}

function DiamondMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="none" aria-hidden="true">
      <rect x="2.5" y="14.5" width="5" height="6.5" rx="1" fill="currentColor" />
      <rect x="9.5" y="10.5" width="5" height="10.5" rx="1" fill="currentColor" />
      <rect x="16.5" y="7" width="5" height="14" rx="1" fill="currentColor" />
      <path d="M19 0.8 22.2 4 19 7.2 15.8 4Z" fill="currentColor" />
      <path d="M15.8 4 H22.2 M19 0.8 V7.2" stroke="#0f0f0f" strokeWidth="0.9" strokeOpacity="0.55" />
    </svg>
  );
}
import {
  SILVER_MONTHLY_PRICE_ID,
  GOLD_MONTHLY_PRICE_ID,
  DIAMOND_MONTHLY_PRICE_ID,
  DIAMOND_MONTHLY_EXPERIMENT_PRICE_ID,
  SILVER_YEARLY_PRICE_ID,
  GOLD_YEARLY_PRICE_ID,
  DIAMOND_YEARLY_PRICE_ID,
  ADDON_VIP_DISCORD_MONTHLY_PRICE_ID,
  ADDON_VIP_DISCORD_YEARLY_PRICE_ID,
} from "@/lib/billing/tier-price-ids";

const silverMonthly: PricingTier = {
  id: "silver",
  kind: "bundle",
  name: "Silver",
  price: 59.99,
  billingPeriod: "monthly",
  priceId: SILVER_MONTHLY_PRICE_ID,
  productType: "ev-plan",
  description:
    "Everything you need to find profitable bets at every major book.",
  includedSummary: "The full Optimizer toolkit",
  chooserSummary: "The full Optimizer toolkit: +EV, arbitrage and live odds",
  excludedNote: "No Whale Watch or +EV Sniper",
  ctaLabel: "Start with Silver",
  trial: true,
  icon: <SilverMark />,
  features: [
    { name: "+EV optimizers: props & game lines", included: true },
    { name: "Arbitrage & low-hold finder", included: true },
    { name: "Esports discrepancies", included: true },
    { name: "Live odds screen, 30+ books", included: true },
  ],
  enabled: true,
};

const goldMonthly: PricingTier = {
  id: "gold",
  kind: "bundle",
  name: "Gold",
  price: 159.99,
  billingPeriod: "monthly",
  priceId: GOLD_MONTHLY_PRICE_ID,
  productType: "ev-plan",
  description: "Track where the largest bets land before the lines move.",
  includedSummary: "Everything in Silver, plus",
  chooserSummary: "Everything in Silver, plus Whale Watch",
  excludedNote: "No +EV Sniper",
  badge: "Most Popular",
  ctaLabel: "Start with Gold",
  trial: true,
  icon: <GoldMark />,
  features: [
    { name: "Whale Watch: live sharp-money feed", included: true },
    { name: "Bet alongside the biggest bettors", included: true },
    { name: "Whale signals across 30+ books", included: true },
    { name: "Historical signal ROI tracking", included: true },
  ],
  enabled: true,
};

const diamondMonthly: PricingTier = {
  id: "diamond",
  kind: "bundle",
  name: "Diamond",
  price: 199.99,
  billingPeriod: "monthly",
  priceId: DIAMOND_MONTHLY_PRICE_ID,
  productType: "ev-plan",
  description:
    "Every +EV bet across every sport, market, and book, ranked on one live board.",
  includedSummary: "Everything in Gold, plus",
  chooserSummary: "Everything in Gold, plus +EV Sniper and VIP Discord",
  savingsNote: "Save ~$60/mo",
  badge: "Best Value",
  highlight: true,
  ctaLabel: "Start with Diamond",
  trial: true,
  icon: <DiamondMark />,
  features: [
    { name: "+EV Sniper: the complete edge board", included: true },
    { name: "Every sport, market & book, ranked by EV%", included: true },
    { name: "Expand any row: book-by-book odds + why", included: true },
    { name: "One-tap to the book or DFS slip", included: true },
    { name: "Race to Place: alerts when a Betr slip is +EV", included: true },
    { name: "VIP Discord: exclusive plays + smart money", included: true },
  ],
  enabled: true,
};

function toYearly(base: PricingTier, price: number, priceId: string): PricingTier {
  return {
    ...base,
    price,
    priceId,
    billingPeriod: "yearly",
    trial: false,
    savingsNote: undefined,
  };
}

const vipDiscordAddon: PricingTier = {
  id: "vip-discord",
  kind: "addon",
  name: "VIP Discord",
  price: 49.99,
  billingPeriod: "monthly",
  priceId: ADDON_VIP_DISCORD_MONTHLY_PRICE_ID,
  productType: "vip-discord",
  description: "Exclusive plays and market reads from vetted sharps, in a private Discord.",
  standaloneNote: "Standalone subscription · cancel anytime",
  isNew: true,
  ctaLabel: "Join the VIP Discord",
  trial: false,
  icon: <DiscordMark className="size-5" />,
  features: [
    { name: "Vetted sharps posting their real plays", included: true },
    { name: "Private picks channel for every sharp", included: true },
    { name: "Included automatically with Diamond", included: true },
  ],
  enabled: true,
};

const diamondMonthlyExperiment: PricingTier = {
  ...diamondMonthly,
  price: 249.99,
  priceId: DIAMOND_MONTHLY_EXPERIMENT_PRICE_ID,
  enabled: false,
};

export const PRICING_TIERS: Array<PricingTier> = [
  silverMonthly,
  toYearly(silverMonthly, 359.99, SILVER_YEARLY_PRICE_ID),
  goldMonthly,
  toYearly(goldMonthly, 799.99, GOLD_YEARLY_PRICE_ID),
  diamondMonthly,
  toYearly(diamondMonthly, 999.99, DIAMOND_YEARLY_PRICE_ID),
  diamondMonthlyExperiment,
  vipDiscordAddon,
  toYearly(vipDiscordAddon, 419.99, ADDON_VIP_DISCORD_YEARLY_PRICE_ID),
];

export const defaultTiers = PRICING_TIERS;

export const comparisonTools: Array<ComparisonTool> = [
  {
    name: "Odds Screen",
    description: "Live odds from 30+ sportsbooks in one view",
    includedIn: ["silver", "gold", "diamond"],
  },
  {
    name: "+EV Optimizer",
    description: "Surface positive-EV props in real time",
    includedIn: ["silver", "gold", "diamond"],
  },
  {
    name: "Game Line Optimizer",
    description: "Best moneyline, spread & total across the board",
    includedIn: ["silver", "gold", "diamond"],
  },
  {
    name: "Prop Discrepancies",
    description: "Cross-Book player-prop line gaps across sports",
    includedIn: ["silver", "gold", "diamond"],
  },
  {
    name: "Arbitrage Tool",
    description: "Lock guaranteed profit across books",
    includedIn: ["silver", "gold", "diamond"],
  },
  {
    name: "Low Hold Tool",
    description: "Find low-vig markets to free up promo flow",
    includedIn: ["silver", "gold", "diamond"],
  },
  {
    name: "Whale Watch",
    description: "Sharp-money tracking across 30+ books",
    includedIn: ["gold", "diamond"],
  },
  {
    name: "+EV Sniper",
    description: "Every +EV edge, ranked on one live board",
    includedIn: ["diamond"],
  },
  {
    name: "Race to Place",
    description: "A push the moment Betr posts a +EV slip",
    includedIn: ["diamond"],
    isNew: true,
  },
  {
    name: "VIP Discord",
    description: "Exclusive plays from bettors who actually win",
    includedIn: ["diamond"],
    isNew: true,
  },
];
